'use client';

import H3 from '@/components/typography/h3';
import Label from '@/components/typography/label';
import P from '@/components/typography/p';
import { gsap } from 'gsap';
import Link from 'next/link';
import { Fragment, useRef } from 'react';
import { useTranslation } from 'react-i18next';

type FoodQuestionItem = {
    title: string;
    description: string;
    cases: { label: string; href: string }[];
};

interface FoodQuestionCardProps {
    item: FoodQuestionItem;
}

export default function FoodQuestionCard({ item }: FoodQuestionCardProps) {
    const { t } = useTranslation();
    const cardRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    const blue = 'oklch(0.8153 0.0919 267.98)';
    const base = 'oklch(0.9764 0.0132 106.64)';

    const handleMouseEnter = () => {
        if (!cardRef.current || !overlayRef.current) return;

        gsap.to(cardRef.current, {
            y: -10,
            duration: 0.3,
            ease: 'power2.out',
            border: '1px solid transparent',
        });

        gsap.to(overlayRef.current, {
            opacity: 1,
            duration: 0.3,
            ease: 'power2.out',
        });
    };

    const handleMouseLeave = () => {
        if (!cardRef.current || !overlayRef.current) return;

        gsap.to(cardRef.current, {
            y: 0,
            duration: 0.6,
            border: '1px solid black',
            ease: 'power2.inOut',
        });

        gsap.to(overlayRef.current, {
            opacity: 0,
            duration: 0.6,
            ease: 'power2.inOut',
        });
    };

    return (
        <div
            ref={cardRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="border-primary relative z-50 flex flex-col gap-[1.87rem] rounded-lg border px-[1.4rem] pt-[1.25rem] pb-[1.4rem]"
            style={{ backgroundColor: base }}
        >
            {/* Hover blue overlay */}
            <div
                ref={overlayRef}
                style={{ backgroundColor: blue }}
                className="pointer-events-none absolute inset-0 z-0 rounded-lg opacity-0"
            />
            {/* Content */}
            <div className="relative z-10 flex flex-col gap-6">
                <H3 className="overflow-hidden text-ellipsis">{item.title}</H3>
                <P>{item.description}</P>
                <p className="overflow-hidden leading-tight text-ellipsis">
                    {item.cases.length > 0 && (
                        <Label variant="button" className="mr-1">
                            {t('foodQuestions.cases')}:
                        </Label>
                    )}
                    {item.cases.map((caseItem, i) => (
                        <Fragment key={i}>
                            <Link href={caseItem.href} className="underline">
                                <Label variant="button" className="inline">
                                    {caseItem.label}
                                </Label>
                            </Link>
                            {i < item.cases.length - 1 && ' / '}
                        </Fragment>
                    ))}
                </p>
            </div>
        </div>
    );
}
